/**
 * useLicense hook
 * Manages license state and provides access to license context
 */

import { useState, useEffect } from 'react';
import { verifyCurrentTenantLicense } from '../services/licenseService';
import type { TenantLicenseContext } from '@shared/dto/license';

export interface LicenseState {
    license: TenantLicenseContext | null;
    isLoading: boolean;
    isValid: boolean;
    isActive: boolean;
    isExpired: boolean;
    error: string | null;
}

export function useLicense() {
    const [state, setState] = useState<LicenseState>({
        license: null,
        isLoading: true,
        isValid: false,
        isActive: false,
        isExpired: false,
        error: null,
    });

    useEffect(() => {
        let isMounted = true;

        async function loadLicense() {
            try {
                const license = await verifyCurrentTenantLicense();

                if (!isMounted) return;

                if (!license) {
                    setState({
                        license: null,
                        isLoading: false,
                        isValid: false,
                        isActive: false,
                        isExpired: false,
                        error: 'Unable to resolve tenant identity',
                    });
                    return;
                }

                setState({
                    license,
                    isLoading: false,
                    isValid: license.status === 'Active' || license.status === 'Expired',
                    isActive: license.status === 'Active',
                    isExpired: license.status === 'Expired',
                    error: null,
                });
            } catch (error) {
                if (!isMounted) return;

                setState({
                    license: null,
                    isLoading: false,
                    isValid: false,
                    isActive: false,
                    isExpired: false,
                    error: error instanceof Error ? error.message : 'Failed to verify license',
                });
            }
        }

        loadLicense();

        return () => {
            isMounted = false;
        };
    }, []);

    return state;
}
